import React, { useRef, useState, useEffect } from "react";

interface OTPInputProps {
  length?: number;
  onSubmit: (otp: string) => Promise<void> | void;
  autoSubmit?: boolean;
  disabled?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onSubmit,
  autoSubmit = true,
  disabled = false,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const isComplete = otp.every((digit) => digit !== "");
  const fullOtp = otp.join("");

  useEffect(() => {
    if (autoSubmit && isComplete) {
      onSubmit(fullOtp);
    }
  }, [otp]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value.replace(/[^0-9]/g, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value[0];
    setOtp(newOtp);

    if (index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData
      .getData("Text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasteData) return;

    const newOtp = [...otp];
    for (let i = 0; i < length; i++) {
      newOtp[i] = pasteData[i] || "";
    }
    setOtp(newOtp);

    const lastFilledIndex = newOtp.findIndex((val) => val === "");
    if (lastFilledIndex === -1) {
      inputsRef.current[length - 1]?.focus();
    } else {
      inputsRef.current[lastFilledIndex]?.focus();
    }
  };

  const handleManualSubmit = () => {
    if (isComplete) {
      onSubmit(fullOtp);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "1rem" }}>
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={(el) => (inputsRef.current[index] = el)}
            disabled={disabled}
            style={{
              width: "40px",
              height: "40px",
              textAlign: "center",
              fontSize: "24px",
              fontWeight: "bold",
              border: "1px solid ##1D40B0",
              borderRadius: "4px",
              background: disabled ? "#f0f0f0" : "white",
            }}
          />
        ))}
      </div>
      {!autoSubmit && (
        <button onClick={handleManualSubmit} disabled={!isComplete || disabled}>
          Submit OTP
        </button>
      )}
    </div>
  );
};
